'use client';

import { useEffect, useState } from 'react';
import type { TenantTier, TenantState } from '@/types';
import { getEmbeddedSession } from './embeddedSessionUtils';

export type TenantContextDto = {
  tenantId: string;
  tenantName: string;
  tier: TenantTier;
  state: TenantState;
  expiresAt?: string | null;
};

type TenantContextResult = {
  context: TenantContextDto | null;
  isLoading: boolean;
};

let cached: TenantContextDto | null | undefined = undefined;
let pendingRefresh: Promise<TenantContextDto | null> | null = null;
const listeners = new Set<() => void>();

function notifyListeners() {
  listeners.forEach((fn) => fn());
}

export function updateTenantContext(patch: Partial<TenantContextDto>): void {
  if (cached) {
    cached = { ...cached, ...patch };
  }
  notifyListeners();
}

// Module-level cache so the fetch only happens once per page load
async function fetchTenantContext(): Promise<TenantContextDto | null> {
  if (cached !== undefined) return cached;
  return refreshTenantContext();
}

/** Force a live re-fetch, bypassing the cache. Concurrent callers (e.g. several 403s arriving at
 *  once) collapse into a single in-flight request instead of each firing their own. */
export function refreshTenantContext(): Promise<TenantContextDto | null> {
  if (pendingRefresh) return pendingRefresh;
  pendingRefresh = (async () => {
    try {
      // The NextAuth cookie is absent in embedded (Salesforce/Chrome) mode - forward the
      // embedded session id so the route handler can authenticate that way instead.
      const embeddedSessionId = typeof window !== 'undefined' ? getEmbeddedSession()?.sessionId : undefined;
      const headers: HeadersInit = embeddedSessionId ? { 'X-Embedded-Session': embeddedSessionId } : {};
      const res = await fetch('/next-api/auth/tenant-context', { credentials: 'include', headers });
      cached = res.ok ? await res.json() : null;
    } catch {
      cached = null;
    }
    notifyListeners();
    return cached!;
  })().finally(() => { pendingRefresh = null; });
  return pendingRefresh;
}

export function useTenantContext(): TenantContextResult {
  const [context, setContext] = useState<TenantContextDto | null>(cached ?? null);
  const [isLoading, setIsLoading] = useState(cached === undefined);

  useEffect(() => {
    if (cached !== undefined) {
      setContext(cached);
      setIsLoading(false);
      return;
    }

    const update = () => {
      setContext(cached ?? null);
      setIsLoading(false);
    };

    listeners.add(update);
    fetchTenantContext();

    return () => {
      listeners.delete(update);
    };
  }, []);

  return { context, isLoading };
}
